package xim.poc.game.configuration.v0.tactics

import xim.math.Vector2f
import xim.math.Vector3f
import xim.poc.UiElementHelper
import xim.poc.audio.SystemSound
import xim.poc.browser.GameKey
import xim.poc.game.*
import xim.poc.game.GameEngine.displayName
import xim.poc.game.configuration.constants.SkillId
import xim.poc.game.configuration.v0.TacticsUnit
import xim.poc.game.configuration.v0.tactics.TacticsSkillPreview.currentJobId
import xim.poc.game.configuration.v0.tactics.TacticsSkillPreview.skillPreview
import xim.poc.ui.ChatLog
import xim.poc.ui.ChatLogColor
import xim.poc.ui.ShiftJis

class TacticsHireUi(val zone: TacticsZoneInstance) {

    private var describedSelf = false
    private lateinit var queryState: UiStateFrame

    fun push() {
        if (!describedSelf && TacticsState.getPlayerUnits().isEmpty()) {
            describedSelf = true
            ChatLog("${ShiftJis.solidSquare}Up to ${ShiftJis.colorAug}4 units${ShiftJis.colorClear} can be deployed in battle!", ChatLogColor.Info)
        }

        queryState = UiStateHelper.openQueryMode(
            promptProvider =  { "Hire which?" },
            optionsProvider = { getOptions() + QueryMenuOption("Cancel", -1) },
            drawFn = this::drawUnit,
            visibleOptions = 9,
            childStates = { listOf(skillPreview) },
            keyHandler = { skillPreviewKeyHandler(skillPreview) }
        ) { handleChoice(it) }
    }

    private fun getOptions(): List<QueryMenuOption> {
        return TacticsState.getHirableJobs().map { QueryMenuOption(it.displayName, it.id.id) }
    }

    private fun handleChoice(queryMenuOption: QueryMenuOption?): QueryMenuResponse {
        if (queryMenuOption == null || queryMenuOption.value < 0) {
            zone.portraitDummy.reset()
            return QueryMenuResponse.pop
        }

        val jobId = TacticsJobId(queryMenuOption.value)
        val jobConfig = TacticsJobConfigs[jobId]

        val random = QueryMenuOption("(Random)", value = 999)

        val nameOptions = listOf(random) + tacticsUnitNames.mapIndexed { i, it -> QueryMenuOption(it, i) }
        UiStateHelper.openQueryMode(
            prompt = "Select a name for the ${ShiftJis.colorItem}${jobConfig.displayName}${ShiftJis.colorClear}.",
            options = nameOptions,
            drawFn = { drawUnit(jobId) },
            visibleOptions = 6,
        ) { handleNameChoice(jobConfig, it) }

        return QueryMenuResponse.noop
    }

    private fun handleNameChoice(jobConfig: TacticsJobConfig, queryMenuOption: QueryMenuOption?): QueryMenuResponse {
        if (queryMenuOption == null || queryMenuOption.value < 0) { return QueryMenuResponse.pop }

        val name = (if (queryMenuOption.value == 999) {
            val existingNames = TacticsState.getPlayerUnits().map { it.name }.toSet()
            tacticsUnitNames.filter { !existingNames.contains(it) }.randomOrNull()
        } else {
            tacticsUnitNames.getOrNull(queryMenuOption.value)
        }) ?: return QueryMenuResponse.pop

        val newUnit = TacticsState.makeUnit(jobConfig.id, name = name)
        TacticsState.addUnit(newUnit)

        ChatLog("Hired ${ShiftJis.colorItem}${newUnit.name}${ShiftJis.colorClear}${ShiftJis.leftBracket}Lv.1 ${jobConfig.displayName}${ShiftJis.rightBracket}!", ChatLogColor.Info)
        return QueryMenuResponse.pop(SystemSound.FishSuccess)
    }

    private fun drawUnit(queryMenuOption: QueryMenuOption) {
        if (queryMenuOption.value < 0) {
            zone.portraitDummy.reset()
            currentJobId = null
            return
        }

        val jobId = TacticsJobId(queryMenuOption.value)
        currentJobId = jobId

        drawUnit(jobId)
    }

    private fun drawUnit(jobId: TacticsJobId) {
        val dummyUnit = TacticsUnit(id = -1, name = "Applicant", currentJob = jobId, jobLevels = mutableMapOf(jobId to JobLevel(0, 1)))
        TacticsUnitDescription.drawUnitDescription(unit = dummyUnit, type = ActorType.AllyNpc, jobId = jobId)
        zone.portraitDummy.show(Vector3f(x=280f,y=8f,z=297f))
    }

}

object TacticsSkillPreview {

    var currentJobId: TacticsJobId? = null

    val skillDescription = UiState(
        focusMenu = "menu    bluehelp",
        additionalDraw = { drawSkillDescription(currentJobId, it) },
        parentRelative = ParentRelative.RightOfParent,
    ) {
        false
    }

    val skillPreview = UiState(
        dynamicFocusMenu = { if (currentJobId == null) { null } else { "menu    merit2ca" } },
        parentRelative = ParentRelative.RightOfParent,
        additionalDraw = { drawLevelUpMoves(currentJobId) },
        drawParent = true,
        childStates = { listOf(skillDescription) },
        scrollSettings = ScrollSettings(numElementsInPage = 11) { getLevelUpSkills(currentJobId).size },
    ) {
        if (UiStateHelper.keyPressed(GameKey.UiLeft) || UiStateHelper.isEscPressed()) {
            UiStateHelper.popState(SystemSound.MenuClose)
            true
        } else {
            false
        }
    }

    private fun getLevelUpSkills(jobId: TacticsJobId?): List<Pair<Int, SkillId>> {
        jobId ?: return emptyList()

        val jobConfig = TacticsJobConfigs[jobId]
        val sorted = jobConfig.skillsByLevel.entries.sortedBy { it.key }

        val entries = ArrayList<Pair<Int, SkillId>>(sorted.size)
        sorted.forEach { for (s in it.value) { entries += (it.key to s) } }

        return entries.filter { it.first <= TacticsState.getMaxLevel() }
    }

    private fun drawSkillDescription(jobId: TacticsJobId?, state: UiState) {
        jobId ?: return
        val levelUpSkills = getLevelUpSkills(jobId)

        val (_, skill) = levelUpSkills.getOrNull(skillPreview.getCurrentItemIndex()) ?: return
        TacticsSkillSelectUi.drawSkillDescription(state = state, skill = skill, actorState = null)
    }

    private fun drawLevelUpMoves(jobId: TacticsJobId?) {
        jobId ?: return
        val levelUpSkills = getLevelUpSkills(jobId)

        val stackPos = skillPreview.latestPosition ?: return
        val scrollSettings = skillPreview.scrollSettings ?: return

        val offset = Vector2f(0f, 0f)

        var counter = 0

        for (i in scrollSettings.lowestViewableItemIndex until scrollSettings.lowestViewableItemIndex + scrollSettings.numElementsInPage) {
            val (level, skill) = levelUpSkills.getOrNull(i) ?: break
            val skillName = skill.displayName()

            val icon = UiElementHelper.getSkillIcon(skill)
            if (icon != null) { UiElementHelper.drawUiElement(lookup = icon.first, index = icon.second, position = offset + stackPos + Vector2f(8f, 4f)) }

            UiElementHelper.drawString(text = "Lv.${level} $skillName", offset = offset + stackPos + Vector2f(28f, 8f))
            offset.y += 16f

            counter += 1
            if (counter >= 11) { return }
        }
    }

}

class TacticsUnitSkillPreview(val unit: TacticsUnit) {

    private val skillDescription = UiState(
        focusMenu = "menu    bluehelp",
        additionalDraw = { drawSkillDescription(it) },
        parentRelative = ParentRelative.RightOfParent,
    ) {
        false
    }

    val skillPreview = UiState(
        dynamicFocusMenu = { "menu    merit2ca" },
        parentRelative = ParentRelative.RightOfParent,
        additionalDraw = { drawSkills() },
        drawParent = true,
        childStates = { listOf(skillDescription) },
        scrollSettings = ScrollSettings(numElementsInPage = 11) { getSkills().size },
    ) {
        if (UiStateHelper.keyPressed(GameKey.UiLeft) || UiStateHelper.isEscPressed()) {
            UiStateHelper.popState(SystemSound.MenuClose)
            true
        } else {
            false
        }
    }

    private fun getSkills(): List<Pair<Int?, SkillId>> {
        val skillsByLevel = ArrayList<Pair<Int?, SkillId>>()

        unit.computeSkills().forEach { skillsByLevel += (null to it) }
        skillsByLevel += getLevelUpSkills().filter { it.first > unit.getCurrentJobLevel().level }

        return skillsByLevel
    }

    private fun getLevelUpSkills(): List<Pair<Int, SkillId>> {
        val jobConfig = unit.getCurrentJobConfig()
        val sorted = jobConfig.skillsByLevel.entries.sortedBy { it.key }

        val entries = ArrayList<Pair<Int, SkillId>>(sorted.size)
        sorted.forEach { for (s in it.value) { entries += (it.key to s) } }

        return entries.filter { it.first <= TacticsState.getMaxLevel() }
    }

    private fun drawSkillDescription(state: UiState) {
        val skills = getSkills()
        val (_, skill) = skills.getOrNull(skillPreview.getCurrentItemIndex()) ?: return
        TacticsSkillSelectUi.drawSkillDescription(state = state, skill = skill, actorState = null)
    }

    private fun drawSkills() {
        val skills = getSkills()

        val stackPos = skillPreview.latestPosition ?: return
        val scrollSettings = skillPreview.scrollSettings ?: return

        val offset = Vector2f(0f, 0f)

        var counter = 0

        for (i in scrollSettings.lowestViewableItemIndex until scrollSettings.lowestViewableItemIndex + scrollSettings.numElementsInPage) {
            val (level, skill) = skills.getOrNull(i) ?: break
            val skillName = skill.displayName()

            val icon = UiElementHelper.getSkillIcon(skill)
            if (icon != null) { UiElementHelper.drawUiElement(lookup = icon.first, index = icon.second, position = offset + stackPos + Vector2f(8f, 4f)) }

            val levelString = if (level != null) { "Lv.${level} " } else { "${ShiftJis.colorItem}" }

            UiElementHelper.drawString(text = "${levelString}$skillName", offset = offset + stackPos + Vector2f(28f, 8f))
            offset.y += 16f

            counter += 1
            if (counter >= 11) { return }
        }
    }

}

fun skillPreviewKeyHandler(context: UiState): Boolean {
    context.resetCursorIndex()

    return if (UiStateHelper.keyPressed(GameKey.UiRight)) {
        UiStateHelper.pushState(context, SystemSound.TargetCycle)
        true
    } else {
        false
    }
}
